import { Button } from "../ui/button";
import { Plus } from "lucide-react";
import { MilestoneEditForm, type TimelineEvent } from "./MilestoneEditForm";
import { MilestoneList } from "./MilestoneList";
import { useTimelineManager } from "./useTimelineManager";

interface AdminTimelineTabProps {
  type: "professional" | "academic" | "community";
  events: TimelineEvent[];
  onRefresh: () => void;
}

function getTabHeaderText(type: string) {
  if (type === "professional") {
    return {
      title: "Professional Career",
      subtitle: "Manage and reorder events shown in the timeline",
    };
  }
  if (type === "academic") {
    return {
      title: "Academic Milestones",
      subtitle: "Manage and reorder events shown in the timeline",
    };
  }
  return {
    title: "Community Volunteering",
    subtitle: "Manage and reorder events shown in the timeline",
  };
}

interface TimelineHeaderProps {
  type: string;
  startAdd: () => void;
  isEditing: boolean;
}

function TimelineHeader({ type, startAdd, isEditing }: TimelineHeaderProps) {
  const { title, subtitle } = getTabHeaderText(type);
  return (
    <div className="flex justify-between items-center pb-2 border-b border-border/80">
      <div>
        <h4 className="text-xl font-bold text-foreground uppercase tracking-wider">{title}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
      {!isEditing && (
        <Button onClick={startAdd} className="bg-brand-primary text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Milestone
        </Button>
      )}
    </div>
  );
}

export function AdminTimelineTab({ type, events, onRefresh }: AdminTimelineTabProps) {
  const filteredEvents = events
    .filter((e) => e.type === type)
    .sort((a, b) => a.sort_order - b.sort_order);

  const {
    editingEvent,
    setEditingEvent,
    detailsEn,
    setDetailsEn,
    detailsTr,
    setDetailsTr,
    activeDraft,
    setActiveDraft,
    handleSaveDraft,
    handleDiscardDraft,
    startAdd,
    startEdit,
    handleSave,
    handleDelete,
    handleMove,
  } = useTimelineManager(type, filteredEvents, onRefresh);

  return (
    <div className="space-y-6">
      <TimelineHeader type={type} startAdd={startAdd} isEditing={!!editingEvent} />

      {editingEvent ? (
        <MilestoneEditForm
          editingEvent={editingEvent}
          setEditingEvent={setEditingEvent}
          detailsEn={detailsEn}
          setDetailsEn={setDetailsEn}
          detailsTr={detailsTr}
          setDetailsTr={setDetailsTr}
          onSave={handleSave}
          onCancel={() => {
            setEditingEvent(null);
            setActiveDraft(null);
          }}
          type={type}
          activeDraft={activeDraft}
          onSaveDraft={handleSaveDraft}
          onDiscardDraft={handleDiscardDraft}
          liveMilestone={filteredEvents.find((e) => e.id === editingEvent.id)}
        />
      ) : (
        <MilestoneList
          events={filteredEvents}
          onMove={handleMove}
          onEdit={startEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

export default AdminTimelineTab;
